const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export interface FeedbackCategory {
  id: string;
  name: string;
  nameEn?: string;
  code: string;
  buildingId: string;
  displayOrder?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  building?: {
    id: string;
    name: string;
  };
  _count?: {
    feedbacks: number;
  };
  responsibleStaff?: { id: string; name: string; email: string }[];
  workflowSteps?: FeedbackWorkflowStep[];
}

export interface FeedbackWorkflowStep {
  id: string;
  stepOrder: number;
  stepName: string;
  statusDescription?: string;
  feedbackStatus: string;
  permissionGroupId?: string;
  slaDuration: number;
  slaUnit: string;
  reminderDuration: number;
  reminderUnit: string;
  assignees: { user: { id: string; name: string; email: string } }[];
  permissionGroup?: { id: string; name: string };
}

export interface FeedbackStats {
  overview: { pending: number; overdueSla: number };
  performance: Record<string, { value: number; change?: number }>;
  charts: {
    openAgeBuckets: { label: string; count: number }[];
    categoryBreakdown: { category: string; count: number; percentage: number }[];
  };
  departmentPerformance: {
    department: string;
    total: number;
    processed: number;
    onTimeSla: number;
    slaViolations: number;
    slaRate: number;
  }[];
  staffPerformance: {
    staffId: string;
    staffName: string;
    department: string;
    total: number;
    processed: number;
    slaRate: number;
  }[];
  averageTimes: {
    firstResponseHours: number;
    averageProcessingHours: number;
    closeRequestHours: number;
    averageSlaViolationHours: number;
  };
  quality: { satisfactionRate: number };
  slaAlerts: { violating: number; approaching: number };
}

export interface Feedback {
  id: string;
  feedbackId: string;
  content: string;
  priority: string;
  status: string;
  tags: string[];
  attachments: string[];
  deadline?: string;
  createdAt: string;
  updatedAt: string;
  confirmedAt?: string;
  completedAt?: string;
  processingStartedAt?: string;
  // Rating fields
  rating?: number;
  ratingComment?: string;
  ratedAt?: string;
  apartment: {
    id: string;
    code: string;
    ownerName: string;
    ownerPhone: string;
    ownerEmail: string;
  };
  building: {
    id: string;
    name: string;
  };
  category: {
    id: string;
    name: string;
    nameEn?: string;
    code: string;
  };
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
  confirmedBy?: {
    id: string;
    name: string;
    email: string;
  };
  completedBy?: {
    id: string;
    name: string;
    email: string;
  };
  assignees?: {
    user: { id: string; name: string; email: string };
  }[];
  processingDuration?: string | null;
  tasks?: FeedbackTask[];
}

export interface FeedbackTask {
  id: string;
  feedbackId: string;
  taskId: string;
  createdAt: string;
  task: {
    id: string;
    title: string;
    description: string;
    status: string;
    dueDate: string;
    completedAt?: string;
    assignedTo: {
      id: string;
      name: string;
      email: string;
    };
  };
}

export interface FeedbackMessage {
  id: string;
  content: string;
  attachments: string[];
  isFromResident: boolean;
  isInternal: boolean;
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
}

export interface CreateFeedbackRequest {
  apartmentId: string;
  categoryId: string;
  content: string;
  priority: string;
  tags: string[];
}

export interface CreateFeedbackMessageRequest {
  content: string;
  isInternal?: boolean;
}

export interface UpdateFeedbackRequest {
  status?: string;
  deadline?: string;
  confirmedById?: string;
}

// Feedback Categories API
export const feedbackCategoriesApi = {
  // Get all feedback categories for a building
  getCategories: async (buildingId: string): Promise<FeedbackCategory[]> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedback-categories?buildingId=${buildingId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch feedback categories');
    }

    return response.json();
  },

  // Create a new feedback category
  createCategory: async (data: Omit<FeedbackCategory, 'id' | 'createdAt' | 'updatedAt'>): Promise<FeedbackCategory> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedback-categories`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create feedback category');
    }

    return response.json();
  },

  // Update a feedback category
  updateCategory: async (id: string, data: Partial<FeedbackCategory>): Promise<FeedbackCategory> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedback-categories/${id}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update feedback category');
    }

    return response.json();
  },

  reorderCategories: async (
    buildingId: string,
    categoryIds: string[],
  ): Promise<FeedbackCategory[]> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedback-categories/reorder`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ buildingId, categoryIds }),
    });

    if (!response.ok) {
      throw new Error('Failed to reorder feedback categories');
    }

    return response.json();
  },

  // Delete a feedback category
  deleteCategory: async (id: string): Promise<void> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedback-categories/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete feedback category');
    }
  },

  getWorkflow: async (categoryId: string): Promise<FeedbackWorkflowStep[]> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedback-categories/${categoryId}/workflow`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch workflow');
    return response.json();
  },

  saveWorkflow: async (categoryId: string, steps: object[]): Promise<FeedbackWorkflowStep[]> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedback-categories/${categoryId}/workflow`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ steps }),
    });
    if (!response.ok) throw new Error('Failed to save workflow');
    return response.json();
  },
};

// Feedbacks API
export const feedbacksApi = {
  // Get all feedbacks with filters
  getFeedbacks: async (params: {
    buildingId?: string;
    status?: string;
    priority?: string;
    categoryId?: string;
    apartmentGroupId?: string;
    apartmentId?: string;
    assigneeId?: string;
    page?: number;
    limit?: number;
    search?: string;
  } = {}): Promise<{
    data: Feedback[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> => {
    const token = localStorage.getItem('token');
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const response = await fetch(`${API_URL}/feedbacks?${searchParams.toString()}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch feedbacks');
    }

    return response.json();
  },

  getListSummary: async (params: {
    buildingId?: string;
    categoryId?: string;
    priority?: string;
    apartmentGroupId?: string;
    apartmentId?: string;
    assigneeId?: string;
  }): Promise<{
    total: number;
    pending: number;
    confirmed: number;
    inProgress: number;
    completed: number;
    rejected: number;
    cancelled: number;
  }> => {
    const token = localStorage.getItem('token');
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const response = await fetch(`${API_URL}/feedbacks/stats/summary?${searchParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch feedback summary');
    }

    return response.json();
  },

  // Get feedback by ID
  getFeedback: async (id: string): Promise<Feedback> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedbacks/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch feedback details');
    }

    return response.json();
  },

  // Create a new feedback
  createFeedback: async (data: CreateFeedbackRequest, files?: File[]): Promise<Feedback> => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    
    // Add feedback data
    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value.toString());
      }
    });

    // Add files
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('files', file);
      });
    }

    const response = await fetch(`${API_URL}/feedbacks`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to create feedback');
    }

    return response.json();
  },

  // Update feedback
  updateFeedback: async (id: string, data: UpdateFeedbackRequest): Promise<Feedback> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedbacks/${id}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update feedback');
    }

    return response.json();
  },

  // Delete feedback
  deleteFeedback: async (id: string): Promise<void> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedbacks/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete feedback');
    }
  },

  // Get feedback messages
  getFeedbackMessages: async (
    feedbackId: string,
    channel?: 'resident' | 'internal',
  ): Promise<FeedbackMessage[]> => {
    const token = localStorage.getItem('token');
    const params = channel ? `?channel=${channel}` : '';
    const response = await fetch(`${API_URL}/feedbacks/${feedbackId}/messages${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch feedback messages');
    }

    return response.json();
  },

  // Add message to feedback
  addFeedbackMessage: async (feedbackId: string, data: CreateFeedbackMessageRequest, files?: File[]): Promise<FeedbackMessage> => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    
    // Add message data
    formData.append('content', data.content);
    if (data.isInternal) {
      formData.append('isInternal', 'true');
    }

    // Add files
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('files', file);
      });
    }

    const response = await fetch(`${API_URL}/feedbacks/${feedbackId}/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to add feedback message');
    }

    return response.json();
  },

  // Confirm feedback
  confirmFeedback: async (id: string, data?: { deadline?: string }): Promise<Feedback> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedbacks/${id}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        status: 'CONFIRMED',
        ...(data?.deadline && { deadline: data.deadline })
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to confirm feedback');
    }
    return response.json();
  },

  // Complete feedback
  completeFeedback: async (id: string): Promise<Feedback> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedbacks/${id}/complete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error('Failed to complete feedback');
    }
    return response.json();
  },

  // Get feedback tasks (sub-tasks)
  getFeedbackTasks: async (feedbackId: string): Promise<FeedbackTask[]> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedbacks/${feedbackId}/tasks`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch feedback tasks');
    }

    return response.json();
  },

  // Add task to feedback (create sub-task)
  addFeedbackTask: async (feedbackId: string, taskId: string): Promise<FeedbackTask> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedbacks/${feedbackId}/tasks`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ taskId }),
    });

    if (!response.ok) {
      throw new Error('Failed to add task to feedback');
    }

    return response.json();
  },

  // Remove task from feedback
  removeFeedbackTask: async (feedbackId: string, taskId: string): Promise<void> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedbacks/${feedbackId}/tasks/${taskId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to remove task from feedback');
    }
  },

  // Update feedback status
  updateFeedbackStatus: async (id: string, status: string): Promise<void> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedbacks/${id}/status`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    });
    if (!response.ok) {
      throw new Error('Failed to update feedback status');
    }
  },

  // Rate feedback
  rateFeedback: async (id: string, rating: number, comment?: string): Promise<Feedback> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/feedbacks/${id}/rate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rating, comment }),
    });
    if (!response.ok) {
      throw new Error('Failed to rate feedback');
    }
    return response.json();
  },

  getStats: async (params: {
    buildingId: string;
    fromDate?: string;
    toDate?: string;
    permissionGroupId?: string;
    staffId?: string;
  }): Promise<FeedbackStats> => {
    const token = localStorage.getItem('token');
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.append(key, value);
    });
    const response = await fetch(`${API_URL}/feedbacks/stats/dashboard?${searchParams}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch feedback stats');
    return response.json();
  },
};

const feedbackApi = { feedbackCategoriesApi, feedbacksApi };
export default feedbackApi; 